import { useEffect, useMemo, useRef, useState } from "react";
import { Clock, AlertTriangle, User, Timer, Play, CheckCircle2, PackageCheck, Loader2, Wrench, Hourglass, Sparkles, Tv, Tablet, PauseCircle, CalendarClock } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Workshop scheduler panel — jobs grouped by stage so the controller can see
// at a glance where each car is (Waiting / In bay / Tech done / Ready).
// Steve's brief: "I need a definite job schedule list that is in the best
// most efficient order… take account of what time the customer wants it
// ready and how long a job takes."
const URGENCY_STYLE = {
  red:   { pill: "bg-destructive/15 text-destructive border-destructive/30", label: "LATE" },
  amber: { pill: "bg-warning/15 text-warning border-warning/30", label: "TIGHT" },
  green: { pill: "bg-success/15 text-success border-success/30", label: "ON TRACK" },
};

// Stage groups rendered top-to-bottom. Order chosen so the SA sees urgent
// action items (Tech done — needs invoicing) at the top of a busy day.
// Colour code (Steve's spec): yellow = checked in / waiting, green = tech
// working, pale blue = on hold (parts / authority), pink = job complete.
const STAGE_GROUPS = [
  { key: "workshop_complete", label: "Job complete — needs SA", icon: Wrench,      accent: "pink",    emptyHint: "No jobs waiting on invoicing." },
  { key: "in_bay",            label: "In the workshop",        icon: Hourglass,   accent: "emerald", emptyHint: "No cars on the lift right now." },
  { key: "waiting",           label: "Checked in — waiting",   icon: Play,        accent: "yellow",  emptyHint: "Nothing left in the queue." },
  { key: "ready",             label: "Ready for collection",   icon: CheckCircle2,accent: "pink",    emptyHint: "Nothing waiting for pickup." },
];

const ACCENT_TITLE = {
  pink:    "text-pink-600 dark:text-pink-400",
  emerald: "text-emerald-600 dark:text-emerald-400",
  yellow:  "text-yellow-600 dark:text-yellow-400",
  sky:     "text-sky-600 dark:text-sky-400",
};

function stageOf(job) {
  if (job.completed_at) return "collected";
  if (job.ready_for_collection_at) return "ready";
  if (job.workshop_complete_at) return "workshop_complete";
  if (job.in_bay_at) return "in_bay";
  return "waiting";
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
}

// Promise (customer OUT) time on the row — comes from the diary import, but
// the advisor can set or override it right here without opening the job.
function PromiseTimeEditor({ job, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(job.promised_ready_time || "");
  const save = async (v) => {
    try {
      await api.patch(`/jobs/${job.id}`, { promised_ready_time: v });
      toast.success(`Out time set — ${v}`);
      setEditing(false);
      onSaved?.();
    } catch {
      toast.error("Couldn't save out time");
    }
  };
  if (editing) {
    return (
      <input
        type="time"
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => (val ? save(val) : setEditing(false))}
        onKeyDown={(e) => { if (e.key === "Enter" && val) save(val); if (e.key === "Escape") setEditing(false); }}
        className="h-6 mt-0.5 text-[11px] rounded border border-border bg-background px-1 font-mono-plex"
        data-testid={`schedule-promise-input-${job.id}`}
      />
    );
  }
  return job.promised_ready_time ? (
    <button
      type="button"
      onClick={() => { setVal(job.promised_ready_time); setEditing(true); }}
      className="text-[10px] text-muted-foreground mt-0.5 hover:text-foreground underline decoration-dotted"
      data-testid={`schedule-promise-edit-${job.id}`}
      title="Customer OUT time from the diary — click to override"
    >
      ready {job.promised_ready_time}
    </button>
  ) : (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="text-[10px] font-black text-warning mt-0.5 underline decoration-dotted uppercase tracking-wide"
      data-testid={`schedule-promise-set-${job.id}`}
      title="No OUT time from the diary — set it now"
    >
      Set out time
    </button>
  );
}

export default function WorkshopSchedulePanel({ jobs, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  // Technician roster for the pre-assign dropdown.
  const [techs, setTechs] = useState([]);
  useEffect(() => {
    api.get("/workshops/me/technicians").then((r) => setTechs(r.data?.technicians || [])).catch(() => {});
  }, []);

  const assignTech = async (jobId, name) => {
    try {
      await api.post(`/jobs/${jobId}/assign-tech`, { name });
      toast.success(name ? `Assigned to ${name}` : "Tech assignment cleared");
      if (name && !techs.includes(name)) setTechs((t) => [...t, name].sort());
      onChanged?.();
    } catch {
      toast.error("Couldn't assign technician");
    }
  };

  // Track which jobs have already fired the "tech done" toast so we don't
  // re-alert the SA on every refresh — only genuine new arrivals.
  const seenWorkshopComplete = useRef(new Set());
  const firstLoadRef = useRef(true);

  useEffect(() => {
    const currentIds = new Set(
      (jobs || [])
        .filter((j) => j.workshop_complete_at && !j.ready_for_collection_at && !j.completed_at)
        .map((j) => j.id),
    );
    if (firstLoadRef.current) {
      currentIds.forEach((id) => seenWorkshopComplete.current.add(id));
      firstLoadRef.current = false;
      return;
    }
    currentIds.forEach((id) => {
      if (!seenWorkshopComplete.current.has(id)) {
        seenWorkshopComplete.current.add(id);
        const j = jobs.find((x) => x.id === id);
        const label = [j?.customer_name, j?.vehicle_reg].filter(Boolean).join(" · ") || "A job";
        toast(`${label} — workshop complete`, {
          description: "Tech has finished. Invoice / QC and press Ready when done.",
          duration: 12000,
        });
      }
    });
  }, [jobs]);

  const transition = async (jobId, action, verb, payload) => {
    setBusyId(jobId);
    try {
      await api.post(`/jobs/${jobId}/${action}`, payload);
      toast.success(verb);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Couldn't ${verb.toLowerCase()}`);
    } finally {
      setBusyId(null);
    }
  };

  // Today's active jobs + carry-overs. Backend already auto-pushes the
  // `appointment_date` to today on any carry-over so they show up in the
  // today filter, but we still peel them into their own section at the top
  // so the SA can see instantly what's been sitting in the workshop overnight.
  const buckets = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const rank = { red: 0, amber: 1, green: 2, grey: 3 };
    const active = (jobs || []).filter((j) => {
      if (["completed", "rejected"].includes(j.status)) return false;
      if (j.is_carried_over) return true;   // always include carry-overs
      const dk = (j.appointment_date || j.appointment_at || j.created_at || "").slice(0, 10);
      return dk === today;
    });
    // Mark's spec (Aug 2026 workshop review): order of importance = whichever
    // car has the earliest latest-safe-start-time. That naturally puts reds
    // (already late) at the top because their start-by is in the past, then
    // ambers (tightest headroom), then greens. Urgency stays as a colour cue
    // on the row, but it no longer drives the sort — start-by does.
    const bySort = (a, b) => {
      const cmp = (a.latest_start_time || "9999").localeCompare(b.latest_start_time || "9999");
      if (cmp !== 0) return cmp;
      const ra = rank[a.schedule_urgency || "grey"];
      const rb = rank[b.schedule_urgency || "grey"];
      return ra - rb;
    };
    const carried = [];
    const grouped = { waiting: [], in_bay: [], workshop_complete: [], ready: [] };
    active.forEach((j) => {
      if (j.is_carried_over) { carried.push(j); return; }
      const s = stageOf(j);
      if (grouped[s]) grouped[s].push(j);
    });
    // Carried-over section sorted by days-in-workshop DESC (worst offender
    // first) then start-by so an "in bay day 3" beats an "on hold day 2".
    carried.sort((a, b) => {
      const dc = (b.carried_over_days || 0) - (a.carried_over_days || 0);
      if (dc !== 0) return dc;
      return bySort(a, b);
    });
    Object.values(grouped).forEach((arr) => arr.sort(bySort));
    return { carried, ...grouped };
  }, [jobs]);

  const totalActive = buckets.carried.length + buckets.waiting.length + buckets.in_bay.length + buckets.workshop_complete.length + buckets.ready.length;

  if (totalActive === 0) {
    return (
      <div className="card-default p-5" data-testid="schedule-panel-empty">
        <div className="label-overline text-primary mb-1">Workshop schedule</div>
        <div className="font-display font-bold text-lg">No jobs booked for today</div>
        <p className="text-sm text-muted-foreground mt-1">
          Import a diary or the schedule sits empty. Every job with a promised-ready time will land here in order.
        </p>
      </div>
    );
  }

  // Top-of-Waiting-queue with real urgency = "START NEXT" pulse. Only one row
  // per day, and only when Waiting is non-empty.
  const nextUpId = (() => {
    const first = buckets.waiting[0];
    if (!first) return null;
    if (!["red", "amber"].includes(first.schedule_urgency)) return null;
    return first.id;
  })();

  const late = ["waiting", "in_bay"].reduce((n, k) => n + buckets[k].filter((r) => r.schedule_urgency === "red").length, 0);
  const tight = ["waiting", "in_bay"].reduce((n, k) => n + buckets[k].filter((r) => r.schedule_urgency === "amber").length, 0);
  const techDoneCount = buckets.workshop_complete.length;

  return (
    <div className="card-default p-5" data-testid="schedule-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="label-overline text-primary">Workshop schedule · today</div>
          <div className="font-display font-bold text-lg">{totalActive} job{totalActive === 1 ? "" : "s"} across the workshop</div>
        </div>
        <div className="flex gap-2 text-xs flex-wrap items-center">
          <Link
            to="/advisor-desk"
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-emerald-600 text-white hover:bg-emerald-500 font-bold no-underline"
            data-testid="schedule-advisor-desk-btn"
            title="Open the touch-screen Advisor Desk — built for a large iPad on the front desk"
          >
            <Tablet className="w-3.5 h-3.5" /> Advisor desk
          </Link>
          <Link
            to="/workshop-wall"
            target="_blank"
            className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md bg-slate-900 text-white hover:bg-slate-700 font-bold no-underline"
            data-testid="schedule-wall-display-btn"
            title="Open the full-screen TV wall display for the workshop"
          >
            <Tv className="w-3.5 h-3.5" /> Wall display
          </Link>
          {techDoneCount > 0 && (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-pink-500/15 text-pink-600 dark:text-pink-400 font-bold ring-2 ring-pink-500/30 animate-pulse" data-testid="schedule-tech-done-count">
              <span className="w-2 h-2 rounded-full bg-pink-500" /> {techDoneCount} job{techDoneCount === 1 ? "" : "s"} complete — invoice now
            </div>
          )}
          {late > 0 && (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-destructive/15 text-destructive font-bold" data-testid="schedule-late-count">
              <AlertTriangle className="w-3 h-3" /> {late} late
            </div>
          )}
          {tight > 0 && (
            <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-warning/15 text-warning font-bold" data-testid="schedule-tight-count">
              <Timer className="w-3 h-3" /> {tight} tight
            </div>
          )}
        </div>
      </div>

      <div className="space-y-5" data-testid="schedule-groups">
        {buckets.carried.length > 0 && (
          <section data-testid="schedule-group-carried-over">
            <header className="flex items-center justify-between gap-2 mb-2">
              <div className="inline-flex items-center gap-2 text-purple-600 dark:text-purple-400">
                <CalendarClock className="w-4 h-4" />
                <span className="text-[11px] font-black uppercase tracking-widest">Carried over from a previous day</span>
                <span className="text-[11px] font-bold tabular-nums opacity-70">· {buckets.carried.length}</span>
              </div>
              <span className="text-[10px] uppercase tracking-wider font-bold text-purple-600 dark:text-purple-400 inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> OUT time auto-pushed to today
              </span>
            </header>
            <ul className="space-y-2">
              {buckets.carried.map((j) => (
                <ScheduleRow
                  key={j.id}
                  job={j}
                  stageKey={stageOf(j)}
                  isNextUp={false}
                  isCarriedOver={true}
                  busy={busyId === j.id}
                  onTransition={transition}
                  techs={techs}
                  onAssignTech={assignTech}
                  onChanged={onChanged}
                />
              ))}
            </ul>
          </section>
        )}
        {STAGE_GROUPS.map((g) => {
          const rows = buckets[g.key] || [];
          if (rows.length === 0) return null;
          const Icon = g.icon;
          const accent = ACCENT_TITLE[g.accent] || "text-foreground";
          return (
            <section key={g.key} data-testid={`schedule-group-${g.key}`}>
              <header className="flex items-center justify-between gap-2 mb-2">
                <div className={`inline-flex items-center gap-2 ${accent}`}>
                  <Icon className="w-4 h-4" />
                  <span className="text-[11px] font-black uppercase tracking-widest">{g.label}</span>
                  <span className="text-[11px] font-bold tabular-nums opacity-70">· {rows.length}</span>
                </div>
                {g.key === "workshop_complete" && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-pink-600 dark:text-pink-400 inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> action needed
                  </span>
                )}
              </header>
              <ul className="space-y-2">
                {rows.map((j) => (
                  <ScheduleRow
                    key={j.id}
                    job={j}
                    stageKey={g.key}
                    isNextUp={j.id === nextUpId}
                    busy={busyId === j.id}
                    onTransition={transition}
                    techs={techs}
                    onAssignTech={assignTech}
                    onChanged={onChanged}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      <p className="text-[11px] text-muted-foreground mt-4">
        Order within each group is <strong className="text-foreground">latest safe start</strong> — the time by which each job must be on a lift to hit its promised-ready time.
        &ldquo;Start by&rdquo; = promised OUT time minus the full job: tech work + 30 min wash + 30 min paperwork.
      </p>
    </div>
  );
}


function ScheduleRow({ job, stageKey, isNextUp, isCarriedOver, busy, onTransition, techs, onAssignTech, onChanged }) {
  const j = job;
  const urg = URGENCY_STYLE[j.schedule_urgency];
  const hold = j.on_hold || null;
  // Colour code: yellow = checked in, green = tech working, pale blue = on
  // hold (parts / authority), pink = job complete / ready. Carried-over
  // jobs get a purple left ring so the SA can spot them at a glance.
  const rowCls = isNextUp
    ? "border-destructive bg-destructive/[0.06] shadow-[0_0_0_2px_hsl(var(--destructive)/0.25)] animate-pulse"
    : isCarriedOver
    ? "border-purple-500/60 bg-purple-500/[0.06] border-l-[6px]"
    : hold
    ? "border-sky-400 bg-sky-400/[0.12]"
    : stageKey === "workshop_complete" || stageKey === "ready"
    ? "border-pink-500 bg-pink-500/[0.08]"
    : stageKey === "in_bay"
    ? "border-emerald-500/70 bg-emerald-500/[0.08]"
    : "border-yellow-500/60 bg-yellow-400/[0.10] hover:border-yellow-500";

  // Live tech / start / ETA — only meaningful once the car's actually in a
  // bay. ETA = in_bay_at + work-only minutes (buffers assumed already baked
  // into the promised time per Steve's brief).
  const inBayIso = j.in_bay_at;
  const workMins = j?.schedule_breakdown?.work_mins || j?.estimated_duration_mins || 60;
  const etaIso = inBayIso ? new Date(new Date(inBayIso).getTime() + workMins * 60_000).toISOString() : null;

  return (
    <li>
      <div className={`p-3 rounded-lg border transition-colors ${rowCls}`} data-testid={`schedule-row-${j.id}`}>
        {/* Top row — status pills (always wrap horizontally so nothing overlaps on phone) */}
        {(isNextUp || urg || hold || isCarriedOver) && (
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {isNextUp && (
              <div className="text-[10px] font-black px-2 py-1 rounded bg-destructive text-destructive-foreground uppercase tracking-wider shrink-0" data-testid="schedule-next-up">
                Start next
              </div>
            )}
            {urg && (
              <div className={`text-[10px] font-bold px-2 py-1 rounded border shrink-0 text-center ${urg.pill}`}>
                {urg.label}
              </div>
            )}
            {hold && (
              <div className="text-[10px] font-black px-2 py-1 rounded bg-sky-400 text-slate-900 uppercase tracking-wider shrink-0 inline-flex items-center gap-1" data-testid={`schedule-row-${j.id}-hold-badge`}>
                <PauseCircle className="w-3 h-3" /> {hold.reason === "parts" ? "Waiting on parts" : "Waiting authority"}
              </div>
            )}
            {isCarriedOver && (
              <span
                className="text-[10px] uppercase tracking-wider font-black px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-600 dark:text-purple-400 border border-purple-500/40 inline-flex items-center gap-1 shrink-0"
                data-testid={`schedule-row-${j.id}-carried-badge`}
                title={`In the workshop since ${j.carried_over_booked_date || ""}`}
              >
                <CalendarClock className="w-3 h-3" />
                Day {j.carried_over_days || 2} · {j.carried_over_reason || "in workshop"}
                {j.carried_over_since_at ? ` since ${fmtTime(j.carried_over_since_at)}` : ""}
              </span>
            )}
          </div>
        )}

        {/* Content — stacks on phone (<sm), horizontal on tablet+ */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Link to={`/jobs/${j.id}`} className="flex-1 min-w-0 no-underline text-inherit">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-medium truncate">{j.customer_name || "Unknown"}</span>
              {j.vehicle_reg && <span className="text-xs text-muted-foreground font-mono-plex">{j.vehicle_reg}</span>}
            </div>
            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
              {(j.work_types || []).length > 0 && (
                <span data-testid={`schedule-row-${j.id}-types`}>
                  {(j.work_types || []).map((t) => t.replace(/_/g, " ")).join(" · ")}
                </span>
              )}
              <span className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {j.estimated_duration_mins || 60} min
              </span>
            </div>
            {/* Live "who / when / ETA" block — only rendered once the car's in a bay */}
            {inBayIso && (stageKey === "in_bay" || stageKey === "workshop_complete") && (
              <div
                className="mt-1.5 inline-flex items-center gap-2 flex-wrap text-[11px] font-medium rounded-md border border-warning/40 bg-warning/[0.08] px-2 py-1"
                data-testid={`schedule-row-${j.id}-tech-progress`}
              >
                <span className="inline-flex items-center gap-1 text-warning">
                  <User className="w-3 h-3" />
                  <span data-testid={`schedule-row-${j.id}-tech-name`}>
                    {j.assigned_technician_name || "Tech"} working on this car
                  </span>
                </span>
                <span className="opacity-40">·</span>
                <span className="inline-flex items-center gap-1 text-foreground">
                  <Play className="w-3 h-3" />
                  <span>started</span>
                  <span className="font-mono-plex font-bold" data-testid={`schedule-row-${j.id}-started-at`}>{fmtTime(inBayIso)}</span>
                </span>
                {etaIso && stageKey === "in_bay" && (
                  <>
                    <span className="opacity-40">·</span>
                    <span className="inline-flex items-center gap-1 text-primary">
                      <Timer className="w-3 h-3" />
                      <span>ETA ready</span>
                      <span className="font-mono-plex font-bold" data-testid={`schedule-row-${j.id}-eta`}>{fmtTime(etaIso)}</span>
                    </span>
                  </>
                )}
              </div>
            )}
            {/* Pre-assign technician — controller picks who's on it before Start */}
            {stageKey === "waiting" && (
              <div
                className="mt-1.5 inline-flex items-center gap-1.5"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
              >
                <User className={`w-3 h-3 ${j.assigned_technician_name ? "text-primary" : "text-muted-foreground"}`} />
                <select
                  value={j.assigned_technician_name || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "__add__") {
                      const name = window.prompt("Technician name");
                      if (name && name.trim()) onAssignTech(j.id, name.trim());
                    } else {
                      onAssignTech(j.id, v);
                    }
                  }}
                  className={`text-[11px] h-7 rounded-md border px-1 bg-background ${j.assigned_technician_name ? "border-primary/50 text-foreground font-bold" : "border-border text-muted-foreground"}`}
                  data-testid={`schedule-assign-tech-${j.id}`}
                >
                  <option value="">Assign tech…</option>
                  {techs.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                  <option value="__add__">+ Add technician…</option>
                </select>
              </div>
            )}
          </Link>

          {/* Start by — full-width row on phone, right-aligned column on tablet+ */}
          <div className="flex items-center justify-between sm:block sm:text-right shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-border/40">
            <div className="text-[10px] label-overline text-muted-foreground">Start by</div>
            <div className="flex items-center gap-2 sm:block">
              <div className="font-mono-plex font-bold text-base sm:text-sm" data-testid={`schedule-row-${j.id}-latest`}>
                {fmtTime(j.latest_start_time)}
              </div>
              {j.latest_start_time_impossible && (
                <div
                  className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-destructive/15 text-destructive border border-destructive/40 sm:mt-1"
                  title="This promise can't be hit — the calculated start is before the workshop opens. Renegotiate the OUT time or schedule an early start."
                  data-testid={`schedule-row-${j.id}-impossible`}
                >
                  Before opening
                </div>
              )}
              {j.promised_out_after_close && !j.latest_start_time_impossible && (
                <div
                  className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-warning/15 text-warning border border-warning/40 sm:mt-1"
                  title="The customer was promised an OUT time after you close. Advisor should rebook or warn overtime."
                  data-testid={`schedule-row-${j.id}-overpromised`}
                >
                  After close
                </div>
              )}
              <PromiseTimeEditor job={j} onSaved={onChanged} />
            </div>
          </div>

          {/* Actions — full-width row on phone, inline on tablet+ */}
          <div className="shrink-0 flex flex-wrap items-center gap-1.5 w-full sm:w-auto">
          {hold ? (
            <button
              type="button"
              onClick={() => onTransition(j.id, "resume", "Job resumed")}
              disabled={busy}
              className="text-xs font-bold inline-flex items-center justify-center gap-1 min-h-11 sm:h-8 px-3 rounded-md bg-sky-500 text-white hover:bg-sky-400 disabled:opacity-50"
              data-testid={`schedule-resume-${j.id}`}
              title="Parts landed / customer authorised — put the job back live"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              Resume
            </button>
          ) : (
            <>
              {(stageKey === "waiting" || stageKey === "in_bay") && (
                <>
                  <button
                    type="button"
                    onClick={() => onTransition(j.id, "hold", "On hold — waiting on parts", { reason: "parts" })}
                    disabled={busy}
                    className="text-[11px] font-bold inline-flex items-center justify-center gap-1 min-h-11 sm:h-8 px-2 sm:px-2 flex-1 sm:flex-none rounded-md border border-sky-400/60 text-sky-600 dark:text-sky-400 hover:bg-sky-400/10 disabled:opacity-50"
                    data-testid={`schedule-hold-parts-${j.id}`}
                    title="Waiting on parts"
                  >
                    <PauseCircle className="w-3.5 h-3.5" /> Parts
                  </button>
                  <button
                    type="button"
                    onClick={() => onTransition(j.id, "hold", "On hold — waiting authority", { reason: "authority" })}
                    disabled={busy}
                    className="text-[11px] font-bold inline-flex items-center justify-center gap-1 min-h-11 sm:h-8 px-2 flex-1 sm:flex-none rounded-md border border-sky-400/60 text-sky-600 dark:text-sky-400 hover:bg-sky-400/10 disabled:opacity-50"
                    data-testid={`schedule-hold-authority-${j.id}`}
                    title="Waiting on customer authority"
                  >
                    <PauseCircle className="w-3.5 h-3.5" /> Auth
                  </button>
                </>
              )}
              {stageKey === "waiting" && (
            <button
              type="button"
              onClick={() => onTransition(j.id, "start-bay", "In bay")}
              disabled={busy}
              className={`text-xs font-bold inline-flex items-center justify-center gap-1 min-h-11 sm:h-8 px-3 flex-1 sm:flex-none rounded-md ${
                isNextUp
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
              } disabled:opacity-50`}
              data-testid={`schedule-start-${j.id}`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              Start
            </button>
          )}
          {stageKey === "in_bay" && (
            <button
              type="button"
              onClick={() => onTransition(j.id, "workshop-complete", "Job complete — SA notified")}
              disabled={busy}
              className="text-xs font-bold inline-flex items-center justify-center gap-1 min-h-11 sm:h-8 px-3 flex-1 sm:flex-none rounded-md bg-pink-600 text-white hover:bg-pink-500 disabled:opacity-50"
              data-testid={`schedule-workshop-complete-${j.id}`}
              title="Tech has finished — alert the service advisor to invoice / QC"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wrench className="w-3.5 h-3.5" />}
              Job complete
            </button>
          )}
          {stageKey === "workshop_complete" && (
            <button
              type="button"
              onClick={() => onTransition(j.id, "mark-ready", "Marked ready — customer notified")}
              disabled={busy}
              className="text-xs font-bold inline-flex items-center justify-center gap-1 min-h-11 sm:h-8 px-3 flex-1 sm:flex-none rounded-md bg-success text-success-foreground hover:bg-success/90 disabled:opacity-50"
              data-testid={`schedule-ready-${j.id}`}
              title="Invoice done — send the customer their 'car is ready' SMS"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
              Ready
            </button>
          )}
          {stageKey === "ready" && (
            <button
              type="button"
              onClick={() => onTransition(j.id, "collect", "Marked collected")}
              disabled={busy}
              className="text-xs font-bold inline-flex items-center justify-center gap-1 min-h-11 sm:h-8 px-3 flex-1 sm:flex-none rounded-md bg-muted text-foreground hover:bg-muted/70 disabled:opacity-50"
              data-testid={`schedule-collect-${j.id}`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5" />}
              Collected
            </button>
          )}
            </>
          )}
          </div>
        </div>
      </div>
    </li>
  );
}
